import { Alert, AlertDescription, AlertTitle, Button } from '@eureca/ui';
import {
  DEFAULT_FAQS_CARD_TITLE,
  defaultFaqsCardCopy,
} from '@/shared/default-faqs/default-faqs-card-copy';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { BlockedEditTooltip } from '@/features/jobs/job-shared/BlockedEditTooltip';
import { RichTextContent } from '@/shared/rich-text-content/RichTextContent';
import { PlusIcon, SquarePenIcon, TrashIcon } from './icons';
import { JobHeaderLogo } from '@/features/jobs/job-shared/JobHeaderLogo';

export interface JobFaq {
  id: string;
  question: string;
  answer: string;
}

export interface JobEditFaqsPageProps {
  jobName: string;
  companyLogoUrl?: string | null;
  companyName?: string | null;
  faqs: readonly JobFaq[];
  isSubmitting: boolean;
  /** EU-569 — vaga Encerrada / Cancelada: modo somente visualização. */
  readOnly?: boolean;
  onAddFaq: () => void;
  onEditFaq: (faqId: string) => void;
  onDeleteFaq: (faqId: string) => void;
  onOpenDefaultFaqs: () => void;
  onBack: () => void;
  onSubmit: () => void;
}

export function JobEditFaqsPage({
  jobName,
  companyLogoUrl,
  companyName,
  faqs,
  isSubmitting,
  readOnly = false,
  onAddFaq,
  onEditFaq,
  onDeleteFaq,
  onOpenDefaultFaqs,
  onBack,
  onSubmit,
}: JobEditFaqsPageProps) {
  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="jobs" />

      <main className="flex flex-1 flex-col items-center gap-8 bg-card px-4 pt-8 pb-16 md:px-8">
        <form
          className="contents"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <header className="flex w-full max-w-[630px] flex-col gap-2">
            <div className="flex flex-row items-center gap-2">
              <JobHeaderLogo logoUrl={companyLogoUrl} companyName={companyName} />
              <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
                {jobName}
              </h1>
            </div>
            <p className="text-xl font-medium leading-none tracking-tight text-accent-foreground md:text-2xl">
              Perguntas Frequentes
            </p>
          </header>

          <div className="flex w-full max-w-[630px] flex-1 flex-col gap-5">
            {/* EU-569 — fieldset nativo desabilita cards (editar/excluir) e
                "Adicionar nova pergunta" em modo visualização; o link
                "Conferir perguntas incluídas" (leitura) fica fora. */}
            <fieldset
              disabled={readOnly}
              className="contents"
              data-testid={readOnly ? 'job-edit-readonly-fieldset' : undefined}
            >
              <BlockedEditTooltip enabled={readOnly}>
                <div className="flex flex-col gap-5">
                  {faqs.map((faq) => (
                    <FaqCard
                      key={faq.id}
                      faq={faq}
                      readOnly={readOnly}
                      onEdit={() => onEditFaq(faq.id)}
                      onDelete={() => onDeleteFaq(faq.id)}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={onAddFaq}
                    disabled={readOnly}
                    className="flex w-full flex-row items-center justify-between rounded-2xl border border-border bg-card px-5 py-5 text-left shadow-[0_1px_2px_-1px_rgba(33,41,40,0.05),0_1px_4px_-1px_rgba(33,41,40,0.05)] transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-50"
                    data-testid="job-faqs-add"
                  >
                    <span className="text-base font-medium leading-none text-foreground">
                      Adicionar nova pergunta
                    </span>
                    <PlusIcon className="size-5 text-foreground" />
                  </button>
                </div>
              </BlockedEditTooltip>
            </fieldset>

            {/* EU-641 — copy por status (FQC-024): editável anuncia a escolha;
                Encerrada/Cancelada informa o bloqueio. Título padronizado. */}
            <Alert className="border-border bg-card">
              <AlertTitle>{DEFAULT_FAQS_CARD_TITLE}</AlertTitle>
              <AlertDescription>
                {defaultFaqsCardCopy('job', readOnly).body}{' '}
                <button
                  type="button"
                  onClick={onOpenDefaultFaqs}
                  className="font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm"
                  data-testid="job-faqs-conferir-default"
                >
                  {defaultFaqsCardCopy('job', readOnly).linkLabel}
                </button>
              </AlertDescription>
            </Alert>

            <div className="mt-auto flex flex-row justify-between gap-2 pt-8">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={onBack}
                data-testid="job-faqs-back"
              >
                Voltar
              </Button>
              {readOnly ? (
                <Button type="button" size="lg" disabled data-testid="job-edit-save-blocked">
                  Salvar alterações
                </Button>
              ) : (
                <Button
                  type="submit"
                  size="lg"
                  loading={isSubmitting}
                  data-testid="job-faqs-submit"
                >
                  Salvar alterações
                </Button>
              )}
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}

interface FaqCardProps {
  faq: JobFaq;
  /** EU-569 — desabilita editar/excluir em modo visualização. */
  readOnly?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function FaqCard({ faq, readOnly = false, onEdit, onDelete }: FaqCardProps) {
  return (
    <article className="flex w-full flex-col gap-5 rounded-2xl border border-border bg-card p-5 shadow-[0_1px_2px_-1px_rgba(33,41,40,0.05),0_1px_4px_-1px_rgba(33,41,40,0.05)]">
      <header className="flex flex-row items-center gap-2">
        <h3 className="flex-1 text-base font-medium leading-none text-foreground">
          {faq.question}
        </h3>
        <button
          type="button"
          aria-label={`Editar ${faq.question}`}
          onClick={onEdit}
          disabled={readOnly}
          className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-50"
          data-testid={`job-faqs-edit-${faq.id}`}
        >
          <SquarePenIcon className="size-5" />
        </button>
        <button
          type="button"
          aria-label={`Excluir ${faq.question}`}
          onClick={onDelete}
          disabled={readOnly}
          className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-50"
          data-testid={`job-faqs-delete-${faq.id}`}
        >
          <TrashIcon className="size-5" />
        </button>
      </header>
      <RichTextContent html={faq.answer} />
    </article>
  );
}
